const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001';

export interface GlossaryTermSummary {
  id: string;
  term: string;
  slug: string;
  definition: string;
  difficulty_hint?: string | null;
  related_terms: string[];
  see_also_video_id?: string | null;
  is_published: boolean;
}

export interface GlossaryTermDetail {
  id: string;
  term: string;
  slug: string;
  definition: string;
  difficulty_hint?: string | null;
  related_terms: { slug: string; term: string }[];
  see_also_video: { id: string; title: string } | null;
  is_published: boolean;
}

export interface GlossaryAdminList {
  items: GlossaryTermSummary[];
  total: number;
}

export interface GlossaryTermCreateRequest {
  term: string;
  definition: string;
  difficulty_hint?: string | null;
  related_terms?: string[];
  see_also_video_id?: string | null;
  is_published?: boolean;
  slug?: string;
}

export type GlossaryTermPatchRequest = Partial<GlossaryTermCreateRequest>;

class GlossaryService {
  private getToken(): string | null {
    return localStorage.getItem('token');
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = this.getToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (options.headers) Object.assign(headers, options.headers as Record<string, string>);

    const response = await fetch(`${API_URL}${endpoint}`, { ...options, headers });

    if (response.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
      throw new Error('Session expired');
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(err.detail || 'Request failed');
    }
    if (response.status === 204) return undefined as T;
    return response.json();
  }

  /** Public — no auth needed. */
  async listPublic(): Promise<GlossaryTermSummary[]> {
    const response = await fetch(`${API_URL}/api/v1/knowledge/glossary`);
    if (!response.ok) return [];
    return response.json();
  }

  async getTermBySlug(slug: string): Promise<GlossaryTermDetail> {
    const response = await fetch(`${API_URL}/api/v1/knowledge/glossary/${slug}`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: 'Term not found' }));
      throw new Error(err.detail || 'Term not found');
    }
    return response.json();
  }

  // Admin endpoints
  async adminList(): Promise<GlossaryAdminList> {
    return this.request('/api/v1/admin/knowledge/glossary');
  }

  async adminCreate(data: GlossaryTermCreateRequest): Promise<GlossaryTermSummary> {
    return this.request('/api/v1/admin/knowledge/glossary', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async adminUpdate(id: string, data: GlossaryTermPatchRequest): Promise<GlossaryTermSummary> {
    return this.request(`/api/v1/admin/knowledge/glossary/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async adminDelete(id: string): Promise<void> {
    return this.request(`/api/v1/admin/knowledge/glossary/${id}`, { method: 'DELETE' });
  }

  async listVideos(): Promise<{ id: string; title: string }[]> {
    const data = await this.request<{ items: { id: string; title: string }[] }>('/api/v1/knowledge/videos');
    return data.items;
  }
}

export const glossaryService = new GlossaryService();
